import { Component, EventEmitter, Input, OnInit, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { Subject, catchError, debounceTime, of, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LogisticsProjectsService } from './logistics-projects.service';
import { LogisticsProjectSummary } from './models/logistics-project.model';
import { SessionStorageService } from '../services/session-storage.service';

@Component({
    selector: 'app-logistics-project-lookup',
    standalone: true,
    imports: [FormsModule, SelectModule],
    styles: [':host { display: block; width: 100%; }'],
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => LogisticsProjectLookupComponent), multi: true }],
    template: `<p-select [inputId]="inputId" [options]="projects" [ngModel]="value" (ngModelChange)="choose($event)"
        [ngModelOptions]="{standalone: true}" optionLabel="projectName" optionValue="projectId"
        [filter]="true" filterBy="projectNumber,projectName" [showClear]="true"
        [disabled]="disabled" [placeholder]="placeholder" [loading]="loading"
        (onFilter)="search.next($event.filter || '')" appendTo="body" styleClass="w-full">
        <ng-template let-project pTemplate="item">
            <div>{{ project.projectNumber || 'Project' }} - {{ project.projectName }}</div>
            <small>{{ project.projectType || project.status || '' }}</small>
        </ng-template>
    </p-select>`
})
export class LogisticsProjectLookupComponent implements ControlValueAccessor, OnInit {
    @Input() inputId?: string;
    @Input() companyId?: number;
    @Input() placeholder = 'Select project';
    @Output() projectSelected = new EventEmitter<LogisticsProjectSummary | null>();
    @Output() projectResolved = new EventEmitter<LogisticsProjectSummary | null>();
    projects: LogisticsProjectSummary[] = [];
    value: number | null = null;
    disabled = false;
    loading = false;
    readonly search = new Subject<string>();
    private onChange: (value: number | null) => void = () => {};
    private onTouched: () => void = () => {};

    constructor(private projectsService: LogisticsProjectsService, private session: SessionStorageService) {
        this.search.pipe(
            debounceTime(200),
            switchMap(term => {
                const companyId = this.companyId || this.session.getCompany()?.id;
                if (!companyId) return of([] as LogisticsProjectSummary[]);
                this.loading = true;
                return this.projectsService.lookupProjects(companyId, term, this.value == null ? [] : [this.value])
                    .pipe(catchError(() => of([] as LogisticsProjectSummary[])));
            }),
            takeUntilDestroyed()
        ).subscribe(projects => {
            this.projects = projects;
            this.loading = false;
            const selected = projects.find(project => project.projectId === this.value);
            if (selected) this.projectResolved.emit(selected);
        });
    }

    ngOnInit(): void { this.search.next(''); }

    writeValue(value: number | null): void {
        this.value = value;
        if (value != null && !this.projects.some(project => project.projectId === value)) this.search.next('');
    }

    registerOnChange(fn: (value: number | null) => void): void { this.onChange = fn; }
    registerOnTouched(fn: () => void): void { this.onTouched = fn; }
    setDisabledState(disabled: boolean): void { this.disabled = disabled; }

    choose(value: number | null): void {
        this.value = value;
        this.onChange(value);
        this.onTouched();
        this.projectSelected.emit(this.projects.find(project => project.projectId === value) || null);
    }
}
